/**
 * Markdown Configuration
 * 
 * Custom component configuration for react-markdown
 * Following the plan's specification for Mantine integration
 */

import React from 'react';
import type { Components } from 'react-markdown';
import { CodeBlock } from '../components/CodeBlock';
import { CitationLink } from '../components/CitationLink';
import { FollowUpQuestion } from '../components/FollowUpQuestion';
import { Table } from '../components/Table';
import { ImageWithDownload } from '../components/ImageWithDownload';
import { CITATION_CONSTANTS } from '../processors/citationProcessor';
import { FOLLOWUP_CONSTANTS } from '../processors/followUpProcessor';
import type { Citation, FollowUpQuestion as FollowUpQuestionType } from '../types';

interface MarkdownConfigOptions {
  citations: Citation[];
  questions: FollowUpQuestionType[];
  onCitationClick?: (citation: Citation) => void;
  onFollowUpClick?: (question: string) => void;
  /**
   * When true, markdown images render with a hoverable
   * download control suitable for assistant messages.
   */
  enableImageDownload?: boolean;
}

const isImageHref = (href?: string): boolean => {
  if (!href) return false;
  try {
    const url = new URL(href, window.location.origin);
    return /\.(png|jpe?g|gif|bmp|svg|webp)$/i.test(url.pathname);
  } catch {
    return /\.(png|jpe?g|gif|bmp|svg|webp)(?:[?#].*)?$/i.test(href);
  }
};

const childrenToText = (children: React.ReactNode): string =>
  React.Children.toArray(children).join('');

export const createMarkdownComponents = (
  options: MarkdownConfigOptions = { citations: [], questions: [] },
): Components => ({
  code({ className, children, inline, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    const language = match ? match[1] : 'text';
    
    return React.createElement(CodeBlock, {
      language,
      inline,
      ...props
    }, String(children).replace(/\n$/, ''));
  },

  a({ href, children, ...props }: any) {
    // Check if this is a citation link (created by our preprocessing)
    if (href?.startsWith(CITATION_CONSTANTS.PREFIX)) {
      const citationIndex = parseInt(href.replace(CITATION_CONSTANTS.PREFIX, ''), 10);
      const citation = options.citations[citationIndex - 1];
      
      if (citation && options.onCitationClick) {
        return React.createElement(CitationLink, {
          index: citationIndex,
          citation,
          onClick: options.onCitationClick
        });
      }
    }

    if (options.enableImageDownload && isImageHref(href)) {
      return React.createElement(ImageWithDownload, {
        src: href,
        alt: childrenToText(children) || 'Generated image',
      });
    }

    // Handle regular links
    return React.createElement('a', { href, ...props }, children);
  },

  blockquote({ children, ...props }: any) {
    const text = children?.toString() || '';
    
    // Check if this is a follow-up question
    if (text.includes(FOLLOWUP_CONSTANTS.PREFIX)) {
      const questionMatch = text.match(new RegExp(`${FOLLOWUP_CONSTANTS.PREFIX}(.+?)${FOLLOWUP_CONSTANTS.SUFFIX}`));
      if (questionMatch && options.onFollowUpClick) {
        const questionText = questionMatch[1];
        const question = options.questions.find(q => q.text === questionText);
        
        if (question) {
          return React.createElement(FollowUpQuestion, {
            question,
            onClick: options.onFollowUpClick
          });
        }
      }
    }

    // Handle regular blockquotes
    return React.createElement('blockquote', props, children);
  },

  table({ children, ...props }: any) {
    return React.createElement(Table, props, children);
  },

  img({ src, alt, ...props }: any) {
    if (options.enableImageDownload) {
      return React.createElement(ImageWithDownload, {
        src,
        alt,
        ...props,
      });
    }

    // Default image rendering when download is not enabled
    // eslint-disable-next-line jsx-a11y/alt-text
    return React.createElement('img', { src, alt, ...props });
  },
});
